import React from 'react';
import {gym_query} from "../../../network/api/gym.api";
import {ticketSubscribe_query} from "../../../network/api/ticketSubscribe.api";
import {getStorage, setStorage} from "../../../helper/storedData/useStorage";
import _ep_CAROUSEL_SIMPLE_LIST_ITEM_SIMPLE_GYM from "./_ep_CAROUSEL_SIMPLE_LIST_ITEM_SIMPLE_GYM";
import _ep_CAROUSEL_SIMPLE_LIST_ITEM_SIMPLE_SUBSCRIBE from "./_ep_CAROUSEL_SIMPLE_LIST_ITEM_SIMPLE_SUBSCRIBE";
import _ep_CAROUSEL_BUTTON_LIST_ITEM from "./_ep_CAROUSEL_BUTTON_LIST_ITEM";

// In-memory query result cache and in-flight request coalescing
const queryResultCache = new Map();
const inFlightQueryPromises = new Map();
const QUERY_CACHE_TTL_MS = 3 * 60 * 1000; // 3 minutes TTL
const QUERY_STORAGE_TTL_SECONDS = 180; // 3 minutes
const QUERY_STORAGE_PREFIX = "CACHE_HOME_QUERY_";

export function clearHomeQueryCache() {
    queryResultCache.clear();
    inFlightQueryPromises.clear();
}

function readPersistedQuery(cacheKey) {
    try {
        const raw = getStorage(QUERY_STORAGE_PREFIX + cacheKey);
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        if (parsed?.timestamp && Date.now() - parsed.timestamp < QUERY_CACHE_TTL_MS) {
            return parsed.data;
        }
    } catch {
        /* ignore */
    }
    return null;
}

function writePersistedQuery(cacheKey, data) {
    try {
        setStorage(
            QUERY_STORAGE_PREFIX + cacheKey,
            JSON.stringify({ timestamp: Date.now(), data }),
            QUERY_STORAGE_TTL_SECONDS
        );
    } catch {
        /* ignore */
    }
}

function quertFixer(queryString) {
    try {
        if (!queryString) return {};
        const regex = /\[date-(hour|day|month)(Before|After)-(\d+)]/g;
        const fixed = queryString.replace(
            regex,
            (_, unit, direction, amount) => {
                const date = new Date();
                const value = Number(amount);
                const sign = direction === 'Before' ? -1 : 1;

                switch (unit) {
                    case 'hour':
                        date.setHours(date.getHours() + sign * value);
                        break;
                    case 'day':
                        date.setDate(date.getDate() + sign * value);
                        break;
                    case 'month':
                        date.setMonth(date.getMonth() + sign * value);
                        break;
                }

                return '"' + date.toISOString().split('T')[0] + '"';
            }
        );
        return { ...JSON.parse(fixed) };
    } catch (ex) {
        try {
            return typeof queryString === 'object' ? queryString : JSON.parse(queryString);
        } catch {
            return {};
        }
    }
}

export function getQueryResults(carousel, error) {
    const firstItem = carousel?.Items?.[0];
    if (!firstItem?.Type?.startsWith("QUERY")) {
        return Promise.resolve(carousel?.Items || []);
    }

    const querySubtype = firstItem?.Type?.split("_")?.[1];
    const fixedQuery = quertFixer(firstItem?.Data);
    const cacheKey = `${querySubtype}_${JSON.stringify(fixedQuery)}`;

    // 1. Check in-memory cache
    const cached = queryResultCache.get(cacheKey);
    if (cached && (Date.now() - cached.timestamp < QUERY_CACHE_TTL_MS)) {
        return Promise.resolve(cached.data);
    }

    const persisted = readPersistedQuery(cacheKey);
    if (persisted) {
        queryResultCache.set(cacheKey, { timestamp: Date.now(), data: persisted });
        return Promise.resolve(persisted);
    }

    // 2. Check in-flight requests (coalescing duplicate concurrent calls)
    if (inFlightQueryPromises.has(cacheKey)) {
        return inFlightQueryPromises.get(cacheKey);
    }

    // 3. Initiate request based on subtype
    let requestPromise;
    switch (querySubtype) {
        case "GYM":
            requestPromise = gym_query(fixedQuery).then(result => {
                return result?.data?.Data?.content || result?.data?.Data || [];
            });
            break;
        case "SUBSCRIBE":
            requestPromise = ticketSubscribe_query(fixedQuery).then(result => {
                return result?.data?.Data?.content || result?.data?.Data || [];
            });
            break;
        case "COUNSELIG":
        case "APPOINTMENT":
        default:
            return Promise.resolve([]);
    }

    // 4. Wrap with cache update and in-flight cleanup
    const coalescedPromise = requestPromise
        .then(content => {
            queryResultCache.set(cacheKey, {
                timestamp: Date.now(),
                data: content
            });
            writePersistedQuery(cacheKey, content);
            inFlightQueryPromises.delete(cacheKey);
            return content;
        })
        .catch(err => {
            inFlightQueryPromises.delete(cacheKey);
            throw err;
        });

    inFlightQueryPromises.set(cacheKey, coalescedPromise);
    return coalescedPromise;
}

export function getListByViewType(type, item, index = 0) {
    if (!item) return null;
    const keySuffix = item.Id != null ? `${item.Id}-${index}` : `${index}`;
    switch (type) {
        case "SIMPLE_GYM":
            return <_ep_CAROUSEL_SIMPLE_LIST_ITEM_SIMPLE_GYM key={"Gym-" + keySuffix} item={item} />;
        case "SIMPLE_SUBSCRIBE":
            return <_ep_CAROUSEL_SIMPLE_LIST_ITEM_SIMPLE_SUBSCRIBE key={"SUBSCRIBE-" + keySuffix} item={item} />;
        case "SIMPLE_SPORT_CAT":
            return <_ep_CAROUSEL_BUTTON_LIST_ITEM key={"SPORT_CAT-" + keySuffix} item={item} />;
        default:
            return null;
    }
}
